// src/app/core/store/policy-analytics/policy-analytics.effects.ts

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { PolicyAnalyticsApiService } from '../../services/policy-analytics-api.service';
import {
  PaProductActions,
  PaConfigActions,
  PaLookupActions,
  PaOverviewActions,
  PaTimeSeriesActions,
  PaBreakdownActions,
  PaRankingsActions,
  PaComparisonActions,
} from './policy-analytics.actions';
import { selectConfigs, selectCitiesLoaded, selectGuaranteeSubtypesLoaded } from './policy-analytics.selectors';

@Injectable()
export class PolicyAnalyticsEffects {
  private readonly actions$   = inject(Actions);
  private readonly api        = inject(PolicyAnalyticsApiService);
  private readonly store      = inject(Store);
  private readonly translate  = inject(TranslateService);

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── Products ──────────────────────────────────────────────────────────────
  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaProductActions.loadProducts),
      switchMap(() =>
        this.api.getProducts().pipe(
          map(products => PaProductActions.loadProductsSuccess({ products })),
          catchError(err => of(PaProductActions.loadProductsFailure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  autoSelectProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaProductActions.loadProductsSuccess),
      filter(({ products }) => products.length > 0),
      map(({ products }) => PaProductActions.selectProduct({ productTypeId: products[0].productTypeId })),
    )
  );

  // ── Config (load on product select, cache check) ──────────────────────────
  loadConfigOnSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaProductActions.selectProduct),
      withLatestFrom(this.store.select(selectConfigs)),
      filter(([{ productTypeId }, configs]) => !configs[productTypeId]),
      map(([{ productTypeId }]) => PaConfigActions.loadConfig({ productTypeId })),
    )
  );

  loadConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaConfigActions.loadConfig),
      switchMap(({ productTypeId }) =>
        this.api.getConfig(productTypeId).pipe(
          map(config => PaConfigActions.loadConfigSuccess({ productTypeId, config })),
          catchError(err => of(PaConfigActions.loadConfigFailure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Lookups (load-once patterns) ──────────────────────────────────────────
  loadCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaLookupActions.loadCities),
      withLatestFrom(this.store.select(selectCitiesLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.api.getCities().pipe(
          map(cities => PaLookupActions.loadCitiesSuccess({ cities })),
        )
      ),
    )
  );

  loadGuaranteeSubtypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaLookupActions.loadGuaranteeSubtypes),
      withLatestFrom(this.store.select(selectGuaranteeSubtypesLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.api.getGuaranteeSubtypes().pipe(
          map(subtypes => PaLookupActions.loadGuaranteeSubtypesSuccess({ subtypes })),
        )
      ),
    )
  );

  // ── Overview ────────────────────────────────────────────────────────────────
  loadOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaOverviewActions.load),
      switchMap(({ request }) =>
        this.api.getOverview(request).pipe(
          map(data => PaOverviewActions.success({ data })),
          catchError(err => of(PaOverviewActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── TimeSeries ──────────────────────────────────────────────────────────────
  loadTimeSeries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaTimeSeriesActions.load),
      switchMap(({ request }) =>
        this.api.getTimeSeries(request).pipe(
          map(data => PaTimeSeriesActions.success({ data })),
          catchError(err => of(PaTimeSeriesActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Breakdown ───────────────────────────────────────────────────────────────
  loadBreakdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaBreakdownActions.load),
      switchMap(({ request }) =>
        this.api.getBreakdown(request).pipe(
          map(data => PaBreakdownActions.success({ data })),
          catchError(err => of(PaBreakdownActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Rankings ────────────────────────────────────────────────────────────────
  loadRankings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaRankingsActions.load),
      switchMap(({ request }) =>
        this.api.getRankings(request).pipe(
          map(data => PaRankingsActions.success({ data })),
          catchError(err => of(PaRankingsActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Comparison ──────────────────────────────────────────────────────────────
  loadComparison$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaComparisonActions.load),
      switchMap(({ request }) =>
        this.api.getComparison(request).pipe(
          map(data => PaComparisonActions.success({ data })),
          catchError(err => of(PaComparisonActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );
}
